import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form as RoccoForm,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '../../../../components/form';
import { Button } from '../../../../components/button';
import { ReactComponent as ArrowRight } from '../../../../icons/svg/arrow/arrow-right.svg';
import { ReactComponent as CheckIcon } from '../../../../icons/svg/check.svg';
import { uiFooterTrackingConfig } from '../Footer';
import { useAtomValue } from 'jotai';
import type { SubscriptionFetcher } from '../EmailSubscriptionForm';

const emailSchema = z.object({
    email: z
        .string()
        .min(1, { message: 'Email address is required' })
        .email({ message: 'Invalid email address' })
        .trim(),
});

type EmailFormInputs = z.infer<typeof emailSchema>;

interface MagazineSubscribeFormProps {
    action: string;
    fetcher: SubscriptionFetcher;
}

const pillClasses =
    'flex h-[4.25rem] w-[22.25rem] items-center justify-between rounded-full border border-white bg-white px-[2.202rem] py-[1.016rem]';

const textClasses =
    'font-normal text-4 text-[#0C0C0C] leading-[1.3] tracking-[0.027rem]';

export const MagazineSubscribeForm: React.FC<MagazineSubscribeFormProps> = ({
    action,
    fetcher,
}) => {
    const { data, state } = fetcher;
    const success = !!data?.success;
    const serverError =
        state === 'idle' && data && !success ? data.message : undefined;

    const [email, setEmail] = useState('');
    const trackingConfig = useAtomValue(uiFooterTrackingConfig);

    useEffect(() => {
        if (success && email) {
            trackingConfig?.onEmailSubscribe(email);
            setEmail('');
        }
    }, [success, email, trackingConfig]);

    const form = useForm<EmailFormInputs>({
        resolver: zodResolver(emailSchema),
        defaultValues: {
            email: '',
        },
    });

    const isSubmitting = state === 'submitting';

    const onSubmit = (formData: EmailFormInputs) => {
        setEmail(formData.email);
        fetcher.submit(formData, { method: 'post', action });
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        void form.handleSubmit(onSubmit)(e);
    };

    if (success) {
        return (
            <div className={pillClasses} role="status" aria-live="polite">
                <span className={textClasses}>You&apos;re subscribed</span>
                <CheckIcon
                    className="size-[1.125rem] fill-[#0C0C0C]"
                    aria-hidden="true"
                />
            </div>
        );
    }

    return (
        <fetcher.Form onSubmit={handleFormSubmit} data-hs-cf-bound="true">
            <RoccoForm {...form}>
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <div className={pillClasses}>
                                <FormControl>
                                    <input
                                        type="email"
                                        placeholder="Email Address"
                                        aria-label="Email address for magazine subscription"
                                        autoComplete="email"
                                        className={`min-w-0 flex-1 bg-transparent ${textClasses} placeholder:text-[#0C0C0C] focus:outline-none`}
                                        {...field}
                                    />
                                </FormControl>
                                <Button
                                    size="square44"
                                    color="transparent"
                                    type="submit"
                                    disabled={isSubmitting}
                                    isLoading={isSubmitting}
                                    aria-label="Subscribe"
                                    className="!h-auto !min-h-0 !w-auto shrink-0 !overflow-visible !p-0"
                                >
                                    <ArrowRight
                                        className="!size-6 shrink-0 fill-[#0C0C0C]"
                                        aria-hidden="true"
                                    />
                                </Button>
                            </div>
                            <div className="mt-2 px-[2.202rem]">
                                <FormMessage>{serverError}</FormMessage>
                            </div>
                        </FormItem>
                    )}
                />
            </RoccoForm>
        </fetcher.Form>
    );
};
